// userCodeFrame.js — mount a project's OWN code in an opaque-origin frame
// (USER-CODE-ORIGIN, canvas H1; design: workspace/docs/user-code-origin-design.md).
//
// RULE (the same as the P0 d16ce904f): no origin that runs user code may hold
// or receive a session. The canvas and the shell hold one, so project code
// must not run on their origin. `createIframeApp` ran `create()` in the PARENT
// heap and wrote DOM into a same-origin about:blank, so a project read the
// parent's storage, DOM and JS, and asked /_session-bridge as the parent
// (canvas/__tests__/boardFrameIsUserCodeOrigin.test.js).
//
// Here the frame is `sandbox` WITHOUT `allow-same-origin`: the browser gives
// it an opaque origin (no storage, no cookie, no reach into the parent, every
// message it sends carries origin "null", which no session-bridge peer check
// accepts). The smbls runtime and the bootstrap boot INSIDE the frame from a
// static srcdoc document; the project arrives as DATA (functions as text) in
// a message; the ONLY line back is a MessageChannel port that serves a fixed,
// project-scoped call table. No host registry, no shared object crosses.
//
// `mountUserCode(frame, data, shell, { path, contextOverrides })` keeps the
// createIframeApp signature, so embedComponent / embedDesignSystem / preview
// renderProject build their shell exactly as before and only the mount moves.
//
// What a consumer may hand the frame, and nothing else:
//   • `frameKit`  — CODE for the frame: `{ functions: { name: fn },
//                   factories: [fn] }`, each SELF-CONTAINED (it crosses as
//                   text into the static bootstrap and runs in the frame realm;
//                   a factory gets `{ Smbls, window }` and returns a bag).
//                   Pure helpers only (design review D1).
//   • `calls`     — the frame -> parent call table `{ name: handler }` plus an
//                   `allow(name, args)` policy (D7). The frame gets a STUB per
//                   name that posts a port message; the handler runs HERE.
//   • the project's own db/fetch adapter (data methods), built in.
// The returned handle (also `getUserCodeHandle(node)`) posts the fixed parent
// -> frame messages (USER_CODE_MESSAGES) and runs editor tools.

import { userCodeFrameBoot } from './userCodeFrameBoot.js'
import { makeEmbedCompatMethods } from './embedCompatMethods.js'
import { makeEmbedModuleStubs, buildEmbedImportMap } from './embedModuleStubs.js'
import { CREATE_CONTEXT_KEYS, spriteHtmlFor, normalizeDesignSystemCase } from './createIframeApp.js'

// The one token list a user-code frame carries. `allow-same-origin` would hand
// the frame its embedder's origin (storage, cookies, parent DOM) — never.
// allow-forms: a project's onSubmit handlers (form-action 'none' still blocks
// the navigation). allow-popups(+escape): project links open in a new tab as
// their own origin. No top navigation. No allow-modals (security review 5): a
// tile could `prompt('Your Symbols password')` over the editor, or loop
// `alert()` and freeze the tab.
export const USER_CODE_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox'

// The DOMQL attr form (`attr: { sandbox: … }`) and the barrel form — a value
// read through the shared functions bag must be a function.
export function userCodeSandbox() {
  return USER_CODE_SANDBOX
}

// Every message type on the boot post and the port, both directions. The
// frame bootstrap is self-contained text and spells the same literals —
// userCodeFrame.test.js pins that the two lists agree.
export const USER_CODE_MESSAGES = Object.freeze({
  // parent -> frame (window.postMessage once, then the port)
  BOOT: 'smbls:user-code-boot',
  MOUNT: 'mount',
  THEME: 'theme',
  DIR: 'dir',
  NAVIGATE: 'navigate',
  TOOL: 'tool',
  TOOL_STOP: 'tool-stop',
  // frame -> parent notifications
  BOOTED: 'booted',
  READY: 'ready',
  BOOT_ERROR: 'boot-error',
  ERROR: 'error',
  TOOL_EVENT: 'tool-event',
  // frame -> parent built-in call: the project's own db/fetch adapter
  DB: 'db'
})
export const USER_CODE_BOOT_TYPE = USER_CODE_MESSAGES.BOOT

const NATIVE_RE = /\{\s*\[native code\]\s*\}/
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype'])
const MAX_DEPTH = 80

const isDomLike = (v) =>
  (typeof Node !== 'undefined' && v instanceof Node) ||
  (typeof Window !== 'undefined' && v instanceof Window) ||
  (!!v && typeof v === 'object' && (v.nodeType > 0 || v.window === v))

// A function as the expression text the frame's deepDestringifyFunctions
// evaluates as `(${text})`. Method shorthand (`name(a) {…}`) is not an
// expression, so it gains a `function` keyword; a getter/setter or native
// function crosses as nothing.
export function functionToExpressionText(fn) {
  let src
  try {
    src = Function.prototype.toString.call(fn)
  } catch {
    return undefined
  }
  if (NATIVE_RE.test(src)) return undefined
  const t = src.trim()
  if (/^(get|set)\s+[\w$]+\s*\(/.test(t)) return undefined
  if (/^(async\s+)?(function\b|class\b|\(|[\w$]+\s*=>)/.test(t)) return t
  if (/^async\s*\(/.test(t)) return t
  const m = /^(async\s+)?(\*?\s*)([\w$]+)\s*\(/.exec(t)
  if (m) return (m[1] || '') + 'function' + (m[2] ? '*' : '') + ' ' + t.slice(m[0].length - 1 - m[3].length)
  return undefined
}

// Copy `v` as structured-clone-safe data for the frame. Functions become
// their expression text (`fnAsText`) or are dropped; DOM nodes, windows,
// symbols, Map/Set/WeakMap, promises and `__`-internal keys are dropped;
// cycles and over-deep branches are cut. Own enumerable data properties only:
// a getter is never read (a proxy-backed state store can throw on reads).
export function serializeForFrame(v, { fnAsText = true } = {}) {
  const seen = new Set()
  const walk = (x, depth) => {
    if (x === null) return null
    const t = typeof x
    if (t === 'string' || t === 'boolean') return x
    if (t === 'number') return Number.isFinite(x) ? x : undefined
    if (t === 'function') return fnAsText ? functionToExpressionText(x) : undefined
    if (t !== 'object') return undefined
    if (x instanceof Date) return Number.isNaN(x.getTime()) ? undefined : x.toISOString()
    if (
      isDomLike(x) ||
      x instanceof Map ||
      x instanceof Set ||
      x instanceof WeakMap ||
      x instanceof WeakSet ||
      x instanceof Promise ||
      x instanceof RegExp ||
      x instanceof Error ||
      ArrayBuffer.isView(x) ||
      x instanceof ArrayBuffer
    )
      return undefined
    if (depth > MAX_DEPTH || seen.has(x)) return undefined
    seen.add(x)
    let out
    if (Array.isArray(x)) {
      out = []
      for (let i = 0; i < x.length; i++) {
        let d
        try {
          d = Object.getOwnPropertyDescriptor(x, i)
        } catch {
          d = null
        }
        const y = d && 'value' in d ? walk(d.value, depth + 1) : undefined
        out.push(y === undefined ? null : y)
      }
    } else {
      out = {}
      let keys
      try {
        keys = Object.keys(x)
      } catch {
        keys = []
      }
      for (const k of keys) {
        if (UNSAFE_KEYS.has(k) || k.startsWith('__')) continue
        let d
        try {
          d = Object.getOwnPropertyDescriptor(x, k)
        } catch {
          d = null
        }
        if (!d || !('value' in d)) continue
        const y = walk(d.value, depth + 1)
        if (y !== undefined) out[k] = y
      }
    }
    seen.delete(x)
    return out
  }
  return walk(v, 0)
}

// ── CSP + the static document ───────────────────────────────────────────────

// A local dev page (localhost / *.localhost / 127.0.0.1). Its project assets
// are served over plain http by the dev servers, which the production CSP
// (https only) would block in every tile. Dev only: a deployed host is never
// local, so production keeps https-only.
export function isLocalDevHost(hostname) {
  const h = String(hostname || '').toLowerCase()
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost')
}
const DEV_SOURCES = 'http://localhost:* http://*.localhost:* http://127.0.0.1:*'

// Nika 2026-10-09 Q3: a tile may call the project's own external APIs, so
// connect is open to https; scripts may load from https (a project's CDN
// dependencies). The frame holds no session, so nothing secret can leave it.
// 'unsafe-eval': the frame revives the project's functions from text.
// `scriptSources`: the per-document hashes (or a nonce) of the inline scripts.
export const buildUserCodeCsp = ({ scriptSources = [], dev = false } = {}) => {
  const d = dev ? ' ' + DEV_SOURCES : ''
  return [
    "default-src 'none'",
    `script-src ${scriptSources.join(' ')} 'unsafe-eval' https: data:${d}`,
    `style-src 'unsafe-inline' https:${d}`,
    `img-src https: data: blob:${d}`,
    `media-src https: data: blob:${d}`,
    `font-src https: data:${d}`,
    `connect-src https: wss:${dev ? ' ' + DEV_SOURCES + ' ws://localhost:* ws://*.localhost:*' : ''}`,
    `frame-src https:${d}`,
    "worker-src 'none'",
    "form-action 'none'",
    "base-uri 'none'"
  ].join('; ')
}

const escAttr = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

// Inside a <script> element only `</script` and `<!--` change how HTML parses
// the text; `\x3C` is `<` in every JS context they can sit in.
const escScript = (s) => String(s).replace(/<(\/script|!--)/gi, '\\x3C$1')

// The frame kit as source text: `{ functions: { name: (fn) }, factories:
// [(fn)] }`. Each function must be self-contained (it runs in the frame realm
// with no closure); a function that cannot cross as text is refused here.
export function frameKitSource(kit) {
  const fns = (kit && kit.functions) || {}
  const factories = (kit && kit.factories) || []
  const parts = []
  for (const name of Object.keys(fns).sort()) {
    if (!/^[A-Za-z_$][\w$]*$/.test(name)) throw new Error('user_code_frame_kit_name')
    const text = typeof fns[name] === 'function' ? functionToExpressionText(fns[name]) : undefined
    if (!text) throw new Error('user_code_frame_kit_fn:' + name)
    parts.push(JSON.stringify(name) + ': (' + text + ')')
  }
  const facs = factories.map((f, i) => {
    const text = typeof f === 'function' ? functionToExpressionText(f) : undefined
    if (!text) throw new Error('user_code_frame_kit_factory:' + i)
    return '(' + text + ')'
  })
  return '{ functions: {' + parts.join(', ') + '}, factories: [' + facs.join(', ') + '] }'
}

export const userCodeBootstrapSource = (kit) =>
  '(' +
  userCodeFrameBoot.toString() +
  ')(window, (' +
  makeEmbedCompatMethods.toString() +
  '), (' +
  makeEmbedModuleStubs.toString() +
  '), ' +
  frameKitSource(kit) +
  ')'

const IMPORT_MAP_TEXT = () => {
  const im = buildEmbedImportMap().match(/^<script type="importmap">([\s\S]*)<\/script>$/)
  return im ? im[1] : ''
}

// STATIC: CSP first, the import map (legacy bare specifiers → stub modules),
// the runtime, the bootstrap. Nothing the project supplies is interpolated
// into HTML; it all travels in messages. With `hashes` the CSP names each
// inline script by its sha256, so EVERY frame of one kind gets the same
// document text (built once, memoized); without, a per-frame nonce.
export const buildUserCodeDocument = ({ runtime = '', bootstrap = '', importMap = '', nonce = null, hashes = null, dev = false }) => {
  const nonceAttr = nonce ? ` nonce="${escAttr(nonce)}"` : ''
  const scriptSources = hashes ? hashes.map((h) => `'sha256-${h}'`) : [`'nonce-${nonce}'`]
  return (
    '<!doctype html><html><head>' +
    `<meta http-equiv="Content-Security-Policy" content="${escAttr(buildUserCodeCsp({ scriptSources, dev }))}">` +
    (importMap ? `<script type="importmap"${nonceAttr}>${importMap}</script>` : '') +
    '<meta name="color-scheme" content="light dark">' +
    '<style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:auto}</style>' +
    `<script${nonceAttr}>${runtime}</script>` +
    `<script${nonceAttr}>${bootstrap}</script>` +
    '</head><body></body></html>'
  )
}

const randomNonce = () => {
  const bytes = new Uint8Array(18)
  globalThis.crypto.getRandomValues(bytes)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const sha256Base64 = async (text) => {
  const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  let s = ''
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b)
  return btoa(s)
}

// The per-page memo, on `scope` (FA409, corrected ruling of the frameworks
// session 2026-10-10; packages/shared has NO globalScope.js) — the page
// root's scope, see pageScope below. Read and written ONLY through the
// element the caller passes in (`opts.el`, or `this` under `el.call`) —
// never a module binding, never `globalThis`. No element (or no scope) = a
// fresh bag for this call only: nothing is memoized, nothing is shared.
//   userCodeRuntimeState: { promise (the smbls IIFE text, a lazy chunk the
//     shell boot path never pays for), docs: Map(bootstrap text -> srcdoc),
//     handles: frame -> live handle, adapters: the host's own Supabase
//     adapters, rates: frame -> its call-rate window (review r2 note 6) }
//   userCodeBootState: { active, waiting } (the boot slot queue)
// `el.scope` is a reactive prototype chain (smbls element/create.js): a NEW
// key written through it lands on the nearest element that declares its own
// `scope` (signal createScopeStore, set trap), so two subtrees would each
// grow a bag and one could not find the other's frame. The memo therefore
// sits on the PAGE ROOT's scope, reached through the element passed in —
// every element's chain reads through to it.
const pageScope = (el) => {
  if (!el) return null
  let root = null
  try {
    root = typeof el.getRoot === 'function' ? el.getRoot() : null
  } catch {}
  const sc = (root && root.scope) || el.scope
  return sc && typeof sc === 'object' ? sc : null
}
const memoBag = (el) => {
  const sc = pageScope(el)
  const fresh = () => ({
    promise: null,
    docs: new Map(),
    handles: new WeakMap(),
    adapters: new Map(),
    rates: new WeakMap()
  })
  if (!sc || typeof sc !== 'object') return fresh()
  if (!sc.userCodeRuntimeState) sc.userCodeRuntimeState = fresh()
  return sc.userCodeRuntimeState
}
const bootBag = (el) => {
  const sc = pageScope(el)
  const fresh = () => ({ active: 0, waiting: [] })
  if (!sc || typeof sc !== 'object') return fresh()
  if (!sc.userCodeBootState) sc.userCodeBootState = fresh()
  return sc.userCodeBootState
}

// The runtime text comes from the workspace SHELL (`el.call(
// 'userCodeRuntimeText')`, registered by packages/workspace only), or from the
// caller (`opts.runtime`). No library imports `smbls/iife-string` (frameworks
// ruling 2026-10-10): frank bundled that dynamic import into every library
// that carried it (PUBLISH BLOCKED — minified names collided with a lifted
// helper), and the 595 KB string stays out of the libraries' published data.
export const loadUserCodeRuntime = (el) => {
  const state = memoBag(el)
  if (!state.promise) {
    const fn = el && el.context && el.context.functions && el.context.functions.userCodeRuntimeText
    state.promise =
      typeof fn === 'function'
        ? Promise.resolve(el.call('userCodeRuntimeText')).then((t) => (typeof t === 'string' ? t : ''))
        : Promise.reject(new Error('user_code_runtime_unavailable'))
    state.promise.catch(() => {
      state.promise = null
    })
  }
  return state.promise
}

// One srcdoc per (runtime, kit): escaped and hashed ONCE per page, the same
// string for every frame of that kind (design review D8). No WebCrypto (an
// insecure context) -> a per-frame nonce document, built each time.
export async function userCodeDocumentFor({ runtime, kit, dev = false, el = null }) {
  const bootstrap = escScript(userCodeBootstrapSource(kit))
  const importMap = escScript(IMPORT_MAP_TEXT())
  const subtle = globalThis.crypto && globalThis.crypto.subtle
  if (!subtle) {
    return buildUserCodeDocument({ runtime: escScript(runtime), bootstrap, importMap, nonce: randomNonce(), dev })
  }
  const state = memoBag(el)
  const key = (dev ? 'dev|' : '|') + bootstrap
  let hit = state.docs.get(key)
  if (!hit || hit.runtime !== runtime) {
    const rt = escScript(runtime)
    hit = {
      runtime,
      doc: Promise.all([sha256Base64(importMap), sha256Base64(rt), sha256Base64(bootstrap)]).then((hashes) =>
        buildUserCodeDocument({ runtime: rt, bootstrap, importMap, hashes, dev })
      )
    }
    state.docs.set(key, hit)
  }
  return hit.doc
}

// Load the runtime and build the document for `frameKit` NOW, so the first
// mount does not pay for them (measured, 23-tile board in a hidden tab: the
// first tile 0.94 s cold vs 0.48 s warm). A consumer calls it as soon as it
// knows it will mount user code — the canvas board at its first tile render,
// while the project data is still loading. Idempotent (the memo bag).
export function prewarmUserCode({ frameKit = null, el = null } = {}) {
  const dev = !!(globalThis.location && isLocalDevHost(globalThis.location.hostname))
  return loadUserCodeRuntime(el)
    .then((runtime) => userCodeDocumentFor({ runtime, kit: frameKit, dev, el }))
    .then(
      () => true,
      () => false
    )
}

// The live handle mounted in `node`, or null. The registry is private to this
// module (design review D6: no expando on the consumer's node).
// `el`: the host element (or `this` under `el.call('getUserCodeHandle', …)`).
export function getUserCodeHandle(node, el) {
  if (!node) return null
  const host = el || this
  const h = memoBag(host).handles.get(node)
  return h && h.alive ? h : null
}

// ── the payload ─────────────────────────────────────────────────────────────

// The project buckets create() reads (CREATE_CONTEXT_KEYS — the record's
// members/payments/secrets never cross), functions as text, the design
// system case-normalized, plus the shell, overrides, route and the NAMES of
// the calls the parent serves (the frame builds a stub per name).
export function buildUserCodePayload(
  data,
  shell,
  { path = '/', contextOverrides = {}, callNames = [] } = {}
) {
  const picked = {}
  for (const key of CREATE_CONTEXT_KEYS) {
    if (Object.hasOwn(data, key)) picked[key] = data[key]
  }
  const project = serializeForFrame(picked) || {}
  if (project.designSystem) normalizeDesignSystemCase(project.designSystem)
  const unset = []
  const overridesIn = {}
  for (const k of Object.keys(contextOverrides || {})) {
    if (contextOverrides[k] === undefined) unset.push(k)
    else overridesIn[k] = contextOverrides[k]
  }
  return {
    project,
    shell: serializeForFrame(shell || {}) || {},
    overrides: serializeForFrame(overridesIn) || {},
    unset,
    path: typeof path === 'string' ? path : '/',
    spriteHtml: spriteHtmlFor(data.designSystem) || '',
    callNames: callNames.filter((n) => typeof n === 'string' && /^[A-Za-z_$][\w$]*$/.test(n)),
    // set by mountUserCode from the HOST-built adapters (B4); never read
    // from anything the project data carries
    adapters: { fetch: false, db: false }
  }
}

// ── the call table (frame → parent) ─────────────────────────────────────────

const DB_METHODS = new Set(['select', 'rpc', 'insert', 'update', 'upsert', 'delete'])
const RATE = { max: 60, windowMs: 1000 }
// One call's arguments, as JSON (security review 6: a 20 MB argument crossed).
export const USER_CODE_MAX_ARGS_BYTES = 256 * 1024
// The only adapter whose methods the parent serves to a frame: the project's
// OWN Supabase, with its own public key (security review B2 — an `sdk`
// adapter is the viewer's session; `rest` is a parent-side fetch proxy).
export const USER_CODE_DB_ADAPTERS = Object.freeze(['supabase'])

// Security review round 2, B4 + B5: the PARENT never calls anything the
// project's data carries (`db.select`, `db.__resolved`, `db.createClient`
// with `__adapterAttached` — a revived project made each one live parent-
// realm code), and its Supabase client never touches the shell's storage.
// The host reads ONLY the plain strings `url` + `key`, builds the client
// itself — memory-only auth (persistSession / autoRefreshToken /
// detectSessionInUrl off, a no-op storage, its own storage key: a client
// with the default storage loaded `sb-<label>-auth-token` from the SHELL's
// localStorage and sent that JWT to a project-chosen host) — and serves only
// an adapter that carries this module's private brand. The name 'supabase'
// alone is not trust.
const HOST_BUILT = Symbol('userCodeHostBuiltAdapter')
const NO_STORAGE = Object.freeze({ getItem: () => null, setItem: () => {}, removeItem: () => {} })

// The plain url + key of a project's own Supabase config, or null.
// `supabaseRef` (host-supplied, optional): the project's real Supabase ref —
// when given, the url host must be `<ref>.supabase.co`.
export function plainSupabaseConfig(cfg, { supabaseRef = null } = {}) {
  if (!cfg || typeof cfg !== 'object' || cfg.adapter !== 'supabase') return null
  const url = cfg.url
  const key = cfg.key
  if (typeof url !== 'string' || typeof key !== 'string' || !key || key.length > 4096) return null
  let u
  try {
    u = new URL(url)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return null
  if (u.pathname !== '/' && u.pathname !== '') return null
  if (typeof supabaseRef === 'string' && supabaseRef && u.hostname !== supabaseRef.toLowerCase() + '.supabase.co') return null
  return { url: u.origin, key }
}

// Build (or reuse, per page) the host's own adapter for a plain config.
// Data methods only, bound to a memory-only client; branded HOST_BUILT.
export async function buildHostSupabaseAdapter(plain, memo) {
  if (!plain) return null
  const cacheKey = plain.url + '|' + plain.key
  const cache = memo && memo.adapters
  if (cache && cache.has(cacheKey)) return cache.get(cacheKey)
  const mod = await import('../supabaseFetchAdapter.js')
  const client = mod.createSupabaseClient(plain.url, plain.key, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storage: NO_STORAGE,
      storageKey: 'user-code-frame-' + Math.random().toString(36).slice(2)
    }
  })
  const full = mod.supabaseAdapter(client)
  const adapter = { name: 'supabase' }
  for (const m of DB_METHODS) if (typeof full[m] === 'function') adapter[m] = full[m].bind(full)
  Object.defineProperty(adapter, HOST_BUILT, { value: true })
  Object.freeze(adapter)
  if (cache) cache.set(cacheKey, adapter)
  return adapter
}
// A table / function name of the project's own backend: no schema dot (no
// `auth.session`, no `auth.users`, no `storage.objects`), no path.
const OWN_TABLE_RE = /^[A-Za-z_][A-Za-z0-9_-]{0,62}$/

// Keys that NAME a scope. A frame never chooses where its data call lands —
// the host pins it (security review B1; the M2 sandbox SCOPE_PARAM_KEYS).
// Compared case-insensitively, at any depth.
export const USER_CODE_SCOPE_KEYS = Object.freeze([
  'workspaceid',
  'workspace',
  'orgid',
  'org',
  'organizationid',
  'organization',
  'projectid',
  'project',
  'ownerid',
  'owner',
  'scope',
  'tenant',
  'tenantid',
  // snake_case twins (security review r2 note 7)
  'workspace_id',
  'org_id',
  'organization_id',
  'project_id',
  'owner_id',
  'tenant_id'
])
// The first scope key found in `value` (any depth), or null.
export function findScopeKey(value, keys = USER_CODE_SCOPE_KEYS) {
  const set = new Set(keys.map((k) => String(k).toLowerCase()))
  const seen = new Set()
  const walk = (v, depth) => {
    if (!v || typeof v !== 'object' || depth > 40 || seen.has(v)) return null
    seen.add(v)
    for (const k of Object.keys(v)) {
      if (set.has(k.toLowerCase())) return k
      const hit = walk(v[k], depth + 1)
      if (hit) return hit
    }
    return null
  }
  return walk(value, 0)
}

const tooLarge = (v) => {
  let n = 0
  try {
    n = JSON.stringify(v === undefined ? null : v).length
  } catch {
    return true
  }
  return n > USER_CODE_MAX_ARGS_BYTES
}

const errorText = (err) =>
  typeof err === 'string' ? err : (err && typeof err.message === 'string' && err.message) || 'error'

// `calls`:   { [name]: handler(...args) } — the consumer's table (D7). Shared
//            knows no app bridge by name; only these names reach the frame.
// `allow`:   (name, args) => true — read at EVERY call, for every name AND for
//            the built-in `db`. No `allow` = every call refused (fail closed,
//            security review 4).
// `adapters`: the project's OWN resolved db/fetch adapters — only a
//            USER_CODE_DB_ADAPTERS one, data methods only, on the project's
//            own tables (no `auth.*`, no schema, no SDK).
// `stamps`:  the rate window of ONE FRAME (review r2 note 6). mountUserCode
//            passes the frame's own array (memo `rates`, keyed by the iframe
//            node), so a remount, a new table, or a teardown + fresh mount of
//            the same frame never resets its budget, and frames never share
//            one. Omitted = a private window for this table only (tests).
// Shared keeps the rate limit, the size cap and the plain-data boundary.
export function createUserCodeCallTable({
  calls = {},
  allow = null,
  adapters = {},
  stamps = [],
  now = () => Date.now()
} = {}) {
  const overRate = () => {
    const t = now()
    while (stamps.length && t - stamps[0] >= RATE.windowMs) stamps.shift()
    if (stamps.length >= RATE.max) return true
    stamps.push(t)
    return false
  }
  const allowed = (name, args) => {
    if (typeof allow !== 'function') return false
    try {
      return allow(name, args) === true
    } catch {
      return false
    }
  }
  return async (method, args) => {
    if (overRate()) throw new Error('user_code_rate_limited')
    if (tooLarge(args)) throw new Error('user_code_args_too_large')
    if (method === USER_CODE_MESSAGES.DB) {
      const a = args && typeof args === 'object' && !Array.isArray(args) ? args : {}
      const adapter = adapters[a.which === 'db' ? 'db' : a.which === 'fetch' ? 'fetch' : '']
      if (
        !adapter ||
        adapter[HOST_BUILT] !== true ||
        !USER_CODE_DB_ADAPTERS.includes(adapter.name) ||
        !DB_METHODS.has(a.method) ||
        typeof adapter[a.method] !== 'function'
      ) {
        throw new Error('user_code_not_allowed')
      }
      const c = a.args && typeof a.args === 'object' ? a.args : {}
      if (typeof c.from !== 'string' || !OWN_TABLE_RE.test(c.from)) throw new Error('user_code_not_allowed')
      if (c.schema !== undefined && c.schema !== 'public') throw new Error('user_code_not_allowed')
      if (!allowed(USER_CODE_MESSAGES.DB, a)) throw new Error('user_code_refused')
      let res
      try {
        res = await adapter[a.method](serializeForFrame(a.args, { fnAsText: false }) || {})
      } catch (err) {
        return { data: null, error: { message: errorText(err) } }
      }
      return {
        data: serializeForFrame(res && res.data, { fnAsText: false }) ?? null,
        error: res && res.error ? { message: errorText(res.error) } : null,
        count: res && Number.isFinite(res.count) ? res.count : null
      }
    }
    if (typeof method !== 'string' || !Object.hasOwn(calls, method) || typeof calls[method] !== 'function') {
      throw new Error('user_code_not_allowed')
    }
    const list = Array.isArray(args) ? serializeForFrame(args, { fnAsText: false }) : []
    if (!allowed(method, list)) throw new Error('user_code_refused')
    const result = await calls[method](...list)
    return serializeForFrame(result, { fnAsText: false }) ?? null
  }
}

// ── the host ────────────────────────────────────────────────────────────────

const tokensOf = (frameNode) => {
  const v = frameNode && frameNode.getAttribute && frameNode.getAttribute('sandbox')
  return typeof v === 'string' ? v.trim().split(/\s+/).filter(Boolean) : null
}

// A frame that loads this many documents WITHOUT one good boot in between
// (the project navigates its frame in a loop) is stopped and reported. A good
// boot resets the count: moving or re-inserting an iframe reloads its srcdoc,
// which is legal (design review D2).
const MAX_LOADS_WITHOUT_BOOT = 4

// At most this many frames BOOT at once (srcdoc parse + runtime + first
// mount). A 23-tile board used to start 23 boots in one tick; they share one
// sandbox renderer process, so they finished together and LATE. Queued boots
// start in mount order — a consumer that mounts what is in view first (the
// canvas viewport gate) gets view order.
export const USER_CODE_BOOT_CONCURRENCY = 3
// A boot that never reports (a frame removed mid-boot) frees its slot here.
const BOOT_SLOT_TIMEOUT_MS = 20000

// Mount `data` (a project, as the data route returns it) in `frameNode`,
// an <iframe>, with the given shell. Returns a promise of
// `{ ok: true, handle }` once the app mounted, or `{ bootError }`.
// A frame that already holds a live user-code document is REMOUNTED over its
// port (no new document, no new runtime parse).
export async function mountUserCode(frameNode, data, shell, opts = {}) {
  if (!frameNode || !data) return null
  const {
    path = '/',
    contextOverrides = {},
    calls = {},
    allow = null,
    frameKit = null,
    // the project's real Supabase ref, when the host knows it (B5)
    supabaseRef = null,
    // the host element whose `scope` holds the per-page memo and the boot
    // queue (FA409 ruling)
    el = null,
    runtime,
    logTag = '',
    onError = null
  } = opts
  const callNames = Object.keys(calls || {})
  const payload = buildUserCodePayload(data, shell, { path, contextOverrides, callNames })
  // the host's OWN adapters, built from the plain url + key strings only —
  // never anything the project data carries (B4)
  const memo = memoBag(el)
  const adapters = {
    fetch: await buildHostSupabaseAdapter(plainSupabaseConfig(data.fetch, { supabaseRef }), memo),
    db: await buildHostSupabaseAdapter(plainSupabaseConfig(data.db, { supabaseRef }), memo)
  }
  payload.adapters = { fetch: !!adapters.fetch, db: !!adapters.db }
  const table = { calls, allow, adapters }

  const live = getUserCodeHandle(frameNode, el)
  if (live) return live.remount(payload, table)

  // The token list is set HERE, before the frame's only navigation (srcdoc),
  // so the sandbox applies whatever the caller's markup said.
  frameNode.setAttribute('sandbox', USER_CODE_SANDBOX)
  const tokens = tokensOf(frameNode)
  if (!tokens || tokens.includes('allow-same-origin')) throw new Error('user_code_sandbox')

  const rt = runtime !== undefined ? runtime : await loadUserCodeRuntime(el)
  const dev = !!(globalThis.location && isLocalDevHost(globalThis.location.hostname))
  const srcdoc = await userCodeDocumentFor({ runtime: rt, kit: frameKit, dev, el })

  // Boot slot (USER_CODE_BOOT_CONCURRENCY), on the same memo bag.
  const boots = bootBag(el)
  if (boots.active >= USER_CODE_BOOT_CONCURRENCY) {
    await new Promise((resolve) => boots.waiting.push(resolve))
  } else {
    boots.active++
  }
  let slotHeld = true
  const releaseSlot = () => {
    if (!slotHeld) return
    slotHeld = false
    const next = boots.waiting.shift()
    if (next) next()
    else boots.active--
  }
  const slotTimer = setTimeout(releaseSlot, BOOT_SLOT_TIMEOUT_MS)
  // Another mount of the same frame may have finished while this one waited.
  const raced = getUserCodeHandle(frameNode, el)
  if (raced) {
    clearTimeout(slotTimer)
    releaseSlot()
    return raced.remount(payload, table)
  }

  const handle = createUserCodeHandle(frameNode, {
    payload,
    table,
    logTag,
    onError,
    srcdoc,
    registry: memo.handles,
    stamps: frameRateWindow(memo, frameNode)
  })
  memo.handles.set(frameNode, handle)
  handle.ready.then(() => {
    clearTimeout(slotTimer)
    releaseSlot()
  })
  frameNode.srcdoc = srcdoc
  return handle.ready
}

// The rate window of one iframe node, for the life of the page (note 6).
const frameRateWindow = (memo, frameNode) => {
  let w = memo.rates.get(frameNode)
  if (!w) memo.rates.set(frameNode, (w = []))
  return w
}

function createUserCodeHandle(frameNode, { payload, table, logTag, onError, srcdoc, registry, stamps }) {
  const M = USER_CODE_MESSAGES
  let current = payload
  let serve = createUserCodeCallTable({ ...table, stamps })
  let port = null
  let loadsSinceBoot = 0
  // Only a document the HOST just set gets a port (security review 7): the
  // first load is ours (srcdoc set at mount); any other load — the project
  // navigated its frame away, or the iframe was moved / re-inserted — first
  // sets our srcdoc again, and the port goes to THAT load.
  let expectOwnDoc = true
  let alive = true
  let warned = false
  let settle = null
  let ready = new Promise((resolve) => (settle = resolve))
  const toolListeners = new Map()

  const say = (message, code) => {
    if (typeof onError === 'function') {
      try {
        onError(message, code)
      } catch {}
      return
    }
    if (warned) return
    warned = true
    console.warn('[user-code frame]', logTag || '', message)
  }

  const resolveReady = (value) => {
    if (settle) {
      const s = settle
      settle = null
      s(value)
    }
  }

  const onPortMessage = (e) => {
    const m = e && e.data
    if (!m || typeof m !== 'object' || typeof m.method !== 'string') return
    if (m.id !== undefined) {
      const id = m.id
      Promise.resolve()
        .then(() => serve(m.method, m.args))
        .then(
          (result) => port && port.postMessage({ id, ok: true, result }),
          (err) => port && port.postMessage({ id, ok: false, error: errorText(err) })
        )
      return
    }
    if (m.method === M.BOOTED) {
      port.postMessage({ type: M.MOUNT, payload: current })
    } else if (m.method === M.READY) {
      loadsSinceBoot = 0
      resolveReady({ ok: true, handle })
    } else if (m.method === M.BOOT_ERROR) {
      // `message` = the thrown error's text; `code` = a bootstrap error code.
      const message = String((m.args && (m.args.message || m.args.code)) || 'boot failed')
      say(message, 'project_boot_failed')
      resolveReady({ bootError: { code: 'project_boot_failed', message } })
    } else if (m.method === M.ERROR) {
      say(String((m.args && m.args.message) || 'error'), 'project_error')
    } else if (m.method === M.TOOL_EVENT) {
      const name = m.args && m.args.name
      const listener = typeof name === 'string' ? toolListeners.get(name) : null
      if (listener) {
        try {
          listener(m.args.data)
        } catch {}
      }
    }
  }

  const boot = () => {
    if (port) {
      try {
        port.onmessage = null
        port.close()
      } catch {}
    }
    const channel = new MessageChannel()
    port = channel.port1
    port.onmessage = onPortMessage
    // The ONLY post with targetOrigin '*': an opaque frame's origin is "null".
    frameNode.contentWindow.postMessage({ type: M.BOOT }, '*', [channel.port2])
  }

  function closePort() {
    if (!port) return
    try {
      port.onmessage = null
      port.close()
    } catch {}
    port = null
  }

  function onLoad() {
    if (!alive) return
    if (!expectOwnDoc) {
      // not our document: never hand it the port — set ours again
      closePort()
      expectOwnDoc = true
      frameNode.srcdoc = srcdoc
      return
    }
    expectOwnDoc = false
    loadsSinceBoot++
    if (loadsSinceBoot > MAX_LOADS_WITHOUT_BOOT) {
      const message = 'the project reloads its frame in a loop; stopped'
      say(message, 'user_code_reload_loop')
      resolveReady({ bootError: { code: 'user_code_reload_loop', message } })
      teardown()
      return
    }
    try {
      boot()
    } catch (err) {
      say(errorText(err), 'user_code_boot')
    }
  }

  function teardown() {
    if (!alive) return
    alive = false
    frameNode.removeEventListener('load', onLoad)
    if (port) {
      try {
        port.onmessage = null
        port.close()
      } catch {}
    }
    port = null
    if (registry && registry.get(frameNode) === handle) registry.delete(frameNode)
    resolveReady(null)
  }

  frameNode.addEventListener('load', onLoad)

  const handle = {
    get alive() {
      return alive
    },
    get ready() {
      return ready
    },
    remount(nextPayload, nextTable) {
      if (!alive) return Promise.resolve(null)
      current = nextPayload
      if (nextTable) serve = createUserCodeCallTable({ ...nextTable, stamps })
      // A mount still waiting for its frame keeps ITS promise: the earlier
      // caller settles with this mount's outcome instead of hanging forever.
      if (!settle) ready = new Promise((resolve) => (settle = resolve))
      if (port) port.postMessage({ type: M.MOUNT, payload: current })
      return ready
    },
    // Run an editor tool inside the frame: `fn` is a SELF-CONTAINED function
    // `({ win, doc, emit }) => stop` (sent as text — no closure survives);
    // `onEvent(data)` receives what it emits. Returns false when not live.
    runTool(name, fn, onEvent) {
      if (!alive || !port || typeof fn !== 'function') return false
      toolListeners.set(name, onEvent)
      port.postMessage({ type: M.TOOL, name, source: Function.prototype.toString.call(fn) })
      return true
    },
    stopTool(name) {
      toolListeners.delete(name)
      if (alive && port) port.postMessage({ type: M.TOOL_STOP, name })
    },
    // A fixed parent -> frame message: THEME { theme }, DIR { dir },
    // NAVIGATE { path }. Anything else is refused.
    post(type, fields = {}) {
      if (!alive || !port) return false
      if (type !== M.THEME && type !== M.DIR && type !== M.NAVIGATE) return false
      port.postMessage(Object.assign({}, serializeForFrame(fields, { fnAsText: false }), { type }))
      return true
    },
    teardown
  }
  return handle
}
